import { GridSortModel } from '@mui/x-data-grid';

import { useQueryState, parseAsInteger, parseAsString } from 'nuqs';
import { useCallback, useState } from 'react';

import { useDebouncedSearch } from './useDebouncedSearch';

type UseTableQueryOptions = {
  defaultSort?: GridSortModel;
  defaultPageSize?: number;
  sortKey?: string;
  paginationKeys?: { page: string; pageSize: string };
  searchKey?: string;
};

export function useTableQuery({
  defaultSort = [],
  defaultPageSize = 100,
  sortKey = '',
  paginationKeys,
  searchKey = ''
}: UseTableQueryOptions = {}) {
  // ======================
  // SEARCH: Controlled Input + Debounced Query
  // ======================

  // The authoritative search value persisted in the URL (via query state)
  const [debouncedSearchText, setDebouncedSearchText] = useQueryState(`${searchKey || 'search'}`, {
    defaultValue: '',
    shallow: false,
    clearOnDefault: true
  });

  // Controlled state for the input field — reflects live user input
  const [search, setSearch] = useState(debouncedSearchText);

  // Debounce handler: triggers update to the query state after delay
  const debouncedFetch = useDebouncedSearch(setDebouncedSearchText, 500);

  const handleSearchTextChange = useCallback(
    (value: string) => {
      debouncedFetch.cancel?.();
      setSearch(value);

      if (value === '') {
        setDebouncedSearchText('');
        return;
      }

      debouncedFetch(value);
    },
    [debouncedFetch, setDebouncedSearchText]
  );
  // ======================
  // PAGINATION
  // ======================

  // Page Number
  const [page, setPage] = useQueryState(
    `${paginationKeys?.page || 'page'}`,
    parseAsInteger.withOptions({ clearOnDefault: true, shallow: false }).withDefault(0)
  );

  // PageSize
  const [pageSize, setPageSize] = useQueryState(
    `${paginationKeys?.pageSize || 'pageSize'}`,
    parseAsInteger
      .withOptions({ clearOnDefault: true, shallow: false })
      .withDefault(defaultPageSize)
  );

  // Pagination Handler
  const handlePaginationChange = useCallback(
    (pagination: { page: number; pageSize: number }) => {
      setPage(pagination.page);
      setPageSize(pagination.pageSize);
    },
    [setPage, setPageSize]
  );

  // ======================
  // SORTING
  // ======================

  const [sortParam, setSortParam] = useQueryState(
    `${sortKey || 'sort'}`,
    parseAsString
      .withOptions({ clearOnDefault: true, shallow: false })
      .withDefault(defaultSort.length > 0 ? `${defaultSort[0].field}:${defaultSort[0].sort}` : '')
  );

  const sortingModel: GridSortModel = sortParam
    ? [
        {
          field: sortParam.split(':')[0],
          sort: sortParam.split(':')[1] as 'asc' | 'desc'
        }
      ]
    : defaultSort;

  // Sorting Handler
  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const nextModel = model.length > 0 ? model : defaultSort;

      if (!nextModel.length) return;

      setPage(0); // Reset page on sort change

      const { field, sort } = nextModel[0];

      setSortParam(`${field}:${sort}`);
    },
    [defaultSort, setPage, setSortParam]
  );

  // ======================
  // RETURN
  // ======================
  return {
    search,
    handleSearchTextChange,
    debouncedSearchText,
    setSearch,
    setDebouncedSearchText,
    page,
    setPage,
    setPageSize,
    pageSize,
    sortingModel,
    handlePaginationChange,
    handleSortModelChange
  };
}
